import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import React, { useEffect, useState } from "react";
import { ChartUtil } from "app/utils/chartUtil";
import {numberFormatter, parseNumberWithCommaDecimal, round, roundWithComma} from "app/utils/stringUtil";
import CountUp from "react-countup";
import {CountUpUtil} from "app/utils/countUpUtil";
import DashboardLoading from "../modules/DashboardLoading";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
import DashboardCard from "../modules/DashboardCard";
import {HighchartsOptionsUtil} from "app/utils/highchartsOptionsUtil";
import {DcUserService} from "../../../services/dc/dcUserService";
import dayjs from "dayjs";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";

const today = dayjs().format('YYYYMMDD');
const lastMonthSameDay = dayjs().subtract(1, "month").format('YYYYMMDD');

const thisMonthDefaultSeries: any = {
  borderWidth: 0,
  type: 'areaspline',
  name: '금월',
  color: '#8FE4D2',
  data: ChartUtil.dayOfMonthArray(today).map(()=>0),
  marker: {
    enabled: false,
    symbol: 'circle',
    lineColor: 'rgba(43,215,246,1)',
    fillColor: 'rgba(43,215,246,1)'
  },
  fillColor: {
    linearGradient: {
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 1
    },
    stops: [
      [-0.1923, '#8EE3D2'], // CSS의 시작점과 색상
      [0.9787, 'rgba(125, 192, 235, 0)']
    ]
  },
};

const lastMonthDefaultSeries: any = {
  type: 'areaspline',
  name: '전월',
  data: ChartUtil.dayOfMonthArray(today).map(()=>0),
  color: '#999',
  marker: {
    enabled: false,
    symbol: 'circle',
    lineColor: '#999',
    fillColor: '#999'
  },
  fillColor: {
    linearGradient: {
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 1
    },
    stops: [
      [0, '#737373'],
      [0.815, 'rgba(217, 217, 217, 0)']
    ]
  },
};

const defaultOptions: Highcharts.Options = {
  chart: {
    type: 'column',
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  noData: HighchartsOptionsUtil.noData,
  tooltip: {
    headerFormat: '<b>{point.x}</b><br/>',
    style: {
      fontSize: '0.75rem',
    },
    formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      let tooltip = `<span><strong style="font-size: 0.75rem;">${this.x}</strong></span><br/>`;
      this.points?.forEach((point: any) => {
        tooltip += `<span style="color:${point.color}">●&nbsp;</span>${point.series.name}: <strong style="font-size: 0.75rem;">${numberFormatter(point.y,2)}</strong> tCO2eq<br/>`;
      });
      return tooltip;
    },
  },
  legend: {
    align: 'right',
    verticalAlign: 'top',
    layout: 'horizontal',
    symbolHeight: 10,
    itemStyle: {
      fontSize: '0.75rem',
      color: '#444444',
    },
  },
  xAxis: {
    categories: ChartUtil.getDayOfMonthCategories(today),
    min: 0,
    crosshair: true,
    gridLineColor: '#CCC',
    gridLineWidth: 1,
    lineColor: '#CCC',
    lineWidth: 1,
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem'
      },
    }
  },
  yAxis: {
    title: {
      text: '(tCO2eq)',
      align: 'high',
      rotation: 0,
      offset: 0,
      y: -25,
      style: {
        color: '#444',
        fontSize: '0.65rem',
      },
    },
    min: 0,
    softMax: 1,
    gridLineColor: '#CCC',
    gridLineWidth: 1,
    labels: {
      x: -5,
      y: 4,
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
      formatter: function (this: any) {
        return parseNumberWithCommaDecimal(this.value);
      },
    },
  },
  series: [thisMonthDefaultSeries, lastMonthDefaultSeries],
  responsive: {
    rules: [{
      condition: {
        maxWidth: 350
      },
      chartOptions: {
        chart: {
          spacingTop: 20,
        },
        xAxis: {
          labels: {
            style: {
              fontSize: '0.5rem',
            },
          },
        },
        yAxis: [
          {
            title: {
              y: -10,
              style: {
                fontSize: '0.5rem',
              },
            },
            labels: {
              style: {
                fontSize: '0.5rem',
              },
            },
          },
        ],
        legend: {
          align: 'right',
          verticalAlign: 'top',
          layout: 'horizontal',
          itemStyle: {
            fontSize: '0.6rem',
          },
        },
        tooltip: {
          style: {
            fontSize: '0.6rem',
          }
        },
      }
    }]
  }
}

export default function DbUserDayCarbonReduction(props: Readonly<DashboardModuleProps>) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<any>();
  const [series, setSeries] = useState<any[]>([thisMonthDefaultSeries, lastMonthDefaultSeries]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [summary, setSummary] = useState<any>();

  useEffect(() => {
    const param = {
      searchType: 'DAY',
      fromDt: dayjs().format('YYYYMMDD') + '000000',
      toDt: dayjs().format('YYYYMMDD') + '235959',
    }

    const paramPrev = {
      searchType: 'DAY',
      fromDt: dayjs().subtract(1, 'month').startOf('month').format('YYYYMMDD') + '000000',
      toDt: dayjs().subtract(1, 'month').endOf('month').format('YYYYMMDD') + '235959',
    }

    const paramMonthly = {
      searchType: 'MONTH',
      fromDt: dayjs().format('YYYYMMDD') + '000000',
      toDt: dayjs().format('YYYYMMDD') + '235959',
    }

    Promise.all([
      DcUserService.getInstance().getDcSplys(param),
      DcUserService.getInstance().getDcSplys(paramPrev),
      DcUserService.getInstance().getDcSplys(paramMonthly)
    ])
    .then(([thisMonthRes, lastMonthRes, monthlyRes]:any) => {
      let thisMonthData: any[] = [];
      let lastMonthData: any[] = [];
      const categories = ChartUtil.getDayOfMonthCategories(today);

      if (thisMonthRes?.data?.length > 0) {
        thisMonthData = ChartUtil.correctMissingDataByCategory2('DAY', thisMonthRes.data, 'dt', 'dcSplyQnt', categories);
      }

      if (lastMonthRes?.data?.length > 0) {
        lastMonthData = ChartUtil.correctMissingDataByCategory2('DAY', lastMonthRes.data, 'dt', 'dcSplyQnt', categories);
      }

      thisMonthData = thisMonthData.map(d => d * 0.4747 / 1000);
      lastMonthData = lastMonthData.map(d => d * 0.4747 / 1000);

      const thisMonthSeries = { ...thisMonthDefaultSeries, data: thisMonthData };
      const lastMonthSeries = { ...lastMonthDefaultSeries, data: lastMonthData };
      const accReduction = monthlyRes?.data?.reduce((sum: any, curr: any) => sum + (curr.dcSplyQnt * 0.4747 / 1000), 0) ?? 0;
      const thisMonthReduction = thisMonthData.reduce((sum, curr) => sum + curr, 0) ?? 0;
      const lastMonthReduction = lastMonthData.reduce((sum, curr) => sum + curr, 0) ?? 0;

      setSeries([thisMonthSeries, lastMonthSeries]);

      setSummary({
        accReduction: accReduction,
        thisMonthReduction: thisMonthReduction,
        lastMonthReduction: lastMonthReduction
      });
    })
    .catch(() => {
      toast.error(t('common.message.error.data'));
    })
    .finally(() => {
      setIsLoading(false);
    });

  }, []);

  useEffect(() => {
    const handleResize = () => {
      setOptions({...defaultOptions, series: series});
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setOptions({
      ...defaultOptions,
      series: series,
    })
  }, [series]);

  return (
    <DashboardCard
      title="월 온실가스 감축량"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl={`/pc/eu/dcgridsply`}
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
        <div className="dash-box-flex">
          <div className="chart">
            {options && (
              <HighchartsReact highcharts={Highcharts} options={options}/>
            )}
          </div>
          <div className="flex-col2">
            <div className="box-style11 pd0">
              <div className="box-in">
                <span>누적 감축량</span>
                <span>
                  <strong>
                    {summary ? (
                      <CountUp
                        end={summary.accReduction ?? 0}
                        decimals={CountUpUtil.getDecimal(round(summary.accReduction ?? 0, 2))}
                        duration={0.5}
                      />
                    ) : (
                      <>-</>
                    )}
                  </strong>
                  <em>tCO2eq</em>
                </span>
              </div>
            </div>
            <div className="list2">
              <span>당월 감축량</span>
              <span>
                <strong>{summary ? roundWithComma(summary.thisMonthReduction ?? 0, 2) : '-'}</strong>
                <em>tCO2eq</em>
              </span>
            </div>
            <div className="list2">
              <span>전월 감축량</span>
              <span>
                <strong>{summary ? roundWithComma(summary.lastMonthReduction ?? 0, 2) : '-'}</strong>
                <em>tCO2eq</em>
              </span>
            </div>
          </div>
        </div>
    </DashboardCard>
  );
}